/**
 * Neighbourhood context keys for the autotile rules (shared by the learner and the runtime).
 *
 * Keys go from most to least specific:
 *   0  own shape + exact shapes of the 8 neighbours
 *   1  own shape + exact side neighbours + corner occupancy bits
 *   2  own shape + side contact codes (neighbour covers shared edge: 0 none, 1 partly, 2 fully) + corners
 *   3  own shape + side contact codes
 *   4  own shape
 */
import { EMPTY, FULL, SHAPE_COUNT, insideShape, sideCoverage } from './shapes.ts';

/** neighbour offsets: L, R, B, T, BL, BR, TL, TR */
export const NB: [number, number][] = [
  [-1, 0], [1, 0], [0, -1], [0, 1],
  [-1, -1], [1, -1], [-1, 1], [1, 1],
];

export const KEY_LEVELS = 5;

const ch = (id: number) => String.fromCharCode(97 + id);

/** contact code of each shape per side [L, R, B, T] */
const CONTACT: number[][] = [];
/** does the shape cover each corner point [BL, BR, TL, TR] */
const CORNER: boolean[][] = [];
for (let id = 0; id < SHAPE_COUNT; id++) {
  CONTACT.push(sideCoverage(id).map((v) => (v <= 0.01 ? 0 : v >= 0.99 ? 2 : 1)));
  const e = 0.02;
  CORNER.push([
    insideShape(id, e, e),
    insideShape(id, 1 - e, e),
    insideShape(id, e, 1 - e),
    insideShape(id, 1 - e, 1 - e),
  ]);
}

/** neighbour side index -> side of the neighbour facing us */
const FACING = [1, 0, 3, 2];
/** diagonal neighbour (index 4..7) -> the neighbour's corner touching our cell */
const FACING_CORNER = [3, 2, 1, 0];

/** Context keys of a cell. `nb` = shapes of the 8 neighbours in NB order. */
export function contextKeys(own: number, nb: ArrayLike<number>, out: string[] = []): string[] {
  const o = ch(own);
  let k0 = 'a' + o;
  for (let i = 0; i < 8; i++) k0 += ch(nb[i]);
  let corners = 0;
  for (let i = 0; i < 4; i++) if (CORNER[nb[4 + i]][FACING_CORNER[i]]) corners |= 1 << i;
  let contacts = '';
  for (let i = 0; i < 4; i++) contacts += CONTACT[nb[i]][FACING[i]];
  const cc = ch(corners);
  out[0] = k0;
  out[1] = 'b' + o + ch(nb[0]) + ch(nb[1]) + ch(nb[2]) + ch(nb[3]) + cc;
  out[2] = 'c' + o + contacts + cc;
  out[3] = 'd' + o + contacts;
  out[4] = 'e' + o;
  return out;
}

/** Cells whose own shape is solid and whose whole 8-neighbourhood is full. */
export function isInterior(own: number, nb: ArrayLike<number>): boolean {
  if (own !== FULL) return false;
  for (let i = 0; i < 8; i++) if (nb[i] !== FULL) return false;
  return true;
}

/** Does any side neighbour leave the shared edge completely uncovered (e.g. the open side of a slope)? */
export function hasOpenSide(nb: ArrayLike<number>): boolean {
  for (let i = 0; i < 4; i++) if (CONTACT[nb[i]][FACING[i]] === 0) return true;
  return false;
}

/** Edge zone: non-empty cells that are not interior (edge art lives here). */
export function isEdgeZone(own: number, nb: ArrayLike<number>): boolean {
  return own !== EMPTY && !isInterior(own, nb);
}

/** Minimum observations for a key level to be trusted before falling back to the next one. */
export const MIN_COUNT = [3, 3, 2, 2, 1];
